import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Box,
  Divider,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  Email,
  Phone,
  LocationOn,
  Event,
  AccessTime,
  Person,
  Business,
  Description,
  AttachMoney,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const InfoItem = ({ icon, label, value }) => (
  <Box display="flex" alignItems="center" gap={1} mb={1}>
    {icon}
    <Box>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  </Box>
);

const SponsorView = ({ currentRecord }) => {
  const [expanded, setExpanded] = useState(false);

  const {
    sponsor_type,
    sponsor_contact,
    sponsor_name,
    sponsor_amount,
    sponsor_description,
    sponsor_logo,
    status,
    event_id,
    createdBy,
  } = currentRecord;

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Card sx={{ maxWidth: "100%", m: 2 }}>
      <CardContent>
        <Grid container spacing={3}>
          {/* Sponsor Header Section */}
          <Grid item xs={12} display="flex" alignItems="center" gap={2}>
            <img
              src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/event/${sponsor_logo}`}
              alt="Sponsor Logo"
              className="object-cover w-20 h-20 border border-black rounded-md"
              crossOrigin="anonymous"
            />
            <Box>
              <Typography variant="h5" gutterBottom>
                {sponsor_name}
              </Typography>
              <Chip
                label={sponsor_type.label}
                color="primary"
                size="small"
                sx={{ mr: 1 }}
              />
              <Chip
                label={status.toUpperCase()}
                color={getStatusColor(status)}
                size="small"
              />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Sponsor Details */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Sponsor Information
            </Typography>
            <InfoItem
              icon={<Business color="primary" />}
              label="Sponsor Type"
              value={sponsor_type.label}
            />
            <InfoItem
              icon={<AttachMoney color="primary" />}
              label="Sponsorship Amount"
              value={`$${sponsor_amount.toLocaleString()}`}
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            <InfoItem
              icon={<Person color="primary" />}
              label="Contact Person"
              value={sponsor_contact.name}
            />
            <InfoItem
              icon={<Email color="primary" />}
              label="Email"
              value={sponsor_contact.email}
            />
            <InfoItem
              icon={<Phone color="primary" />}
              label="Phone"
              value={sponsor_contact.phone}
            />
          </Grid>

          {/* Description - Full Width */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Description
            </Typography>
            <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1 }}>
              <Typography variant="body1">{sponsor_description}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Collapsible Event Information */}
          <Grid item xs={12}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" gutterBottom>
                  Event Details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {event_id.title} •{" "}
                  {format(new Date(event_id.event_start_date), "PPP")}
                </Typography>
              </Box>
              <IconButton onClick={handleExpandClick}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Description color="primary" />}
                    label="Event Type"
                    value={event_id.event_type.label}
                  />
                  <InfoItem
                    icon={<AccessTime color="primary" />}
                    label="Event Time"
                    value={`${event_id.event_start_time} - ${event_id.event_end_time}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<LocationOn color="primary" />}
                    label="Location"
                    value={event_id.event_location}
                  />
                  <InfoItem
                    icon={<Person color="primary" />}
                    label="Created By"
                    value={`${createdBy.first_name} ${createdBy.last_name}`}
                  />
                  <InfoItem
                    icon={<Phone color="primary" />}
                    label="Event Contact"
                    value={event_id.event_contact_number}
                  />
                </Grid>
              </Grid>
            </Collapse>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default SponsorView;
